import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Home.css';

const REASONS = [
  {
    img: '/assets/traveling.jpg',
    title: 'Viajar...',
    text: 'O inglês é o idioma "oficial" do mundo. Não importa onde você vá, você precisa saber o básico para se comunicar. Vai viajar? Aprenda inglês!',
  },
  {
    img: '/assets/work.jpg',
    title: 'Trabalhar...',
    text: 'Hoje, falar inglês é quase obrigatório para se destacar no mercado e trabalhar em empresas que pagam bem. Saia na frente da concorrência!',
  },
  {
    img: '/assets/studying.jpg',
    title: 'Você ama o inglês...',
    text: 'Fascinado pelo idioma? Aprenda inglês para se comunicar com pessoas do mundo inteiro e ter acesso a uma infinidade de conteúdos.',
  },
  {
    img: '/assets/studyinguniv.jpg',
    title: 'Educação de qualidade...',
    text: 'As melhores universidades do mundo ficam em países de língua inglesa. Para estudar nelas, você precisa dominar o idioma.',
  },
  {
    img: '/assets/listening.jpg',
    title: 'Ah! Aquela música!',
    text: 'Quando você finalmente entende a letra daquela música favorita em inglês, a vontade é de cantar bem alto. Que charme!',
  },
  {
    img: '/assets/friends.jpg',
    title: 'Amigos gringos...',
    text: 'Com amigos de outras culturas você se torna uma pessoa mais confiante e aberta ao mundo. O inglês vai abrir portas pra você!',
  },
];

export default function Home() {
  const [produtos, setProdutos] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [palavraDoDia, setPalavraDoDia] = useState(null);
  const [palavraAudio, setPalavraAudio] = useState(false);
  const { user } = useAuth();
  const cardsRef        = useRef(null);
  const presentRef      = useRef(null);
  const ctaRef          = useRef(null);
  const testimonialsRef = useRef(null);

  useEffect(() => {
    api.get('/produtos').then(r => setProdutos(r.data)).catch(() => {});
    api.get('/testimonials').then(r => setTestimonials(r.data)).catch(() => {});
    api.get('/palavra-do-dia').then(r => setPalavraDoDia(r.data)).catch(() => {});
  }, []);

  function playPalavra(text) {
    if (palavraAudio) return;
    setPalavraAudio(true);
    fetch('/synt/text-to-speech', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, languageCode: 'en-US', voiceName: 'en-US-Neural2-J', pitch: 0 }),
    }).then(r => r.blob()).then(blob => {
      const audio = new Audio(URL.createObjectURL(blob));
      audio.onended = () => setPalavraAudio(false);
      audio.play();
    }).catch(() => setPalavraAudio(false));
  }

  // Scroll reveal — cards "Por que inglês"
  useEffect(() => {
    if (!cardsRef.current) return;
    const obs = new IntersectionObserver(
      entries => entries.forEach((e, i) => {
        if (e.isIntersecting) {
          setTimeout(() => e.target.classList.add('visible'), i * 110);
          obs.unobserve(e.target);
        }
      }),
      { threshold: 0.12 }
    );
    cardsRef.current.querySelectorAll('.home-card-en').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Slide-in — seção de apresentação (imagem + texto)
  // threshold baixo (0.08) garante que dispara mesmo em telas pequenas
  useEffect(() => {
    if (!presentRef.current) return;
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          obs.unobserve(e.target);
        }
      }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    presentRef.current
      .querySelectorAll('.home-present-img, .home-present-text')
      .forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Fade-up — testemunhos
  useEffect(() => {
    if (!testimonialsRef.current || testimonials.length === 0) return;
    const obs = new IntersectionObserver(
      entries => entries.forEach((e, i) => {
        if (e.isIntersecting) {
          setTimeout(() => e.target.classList.add('visible'), i * 100);
          obs.unobserve(e.target);
        }
      }),
      { threshold: 0.1 }
    );
    testimonialsRef.current.querySelectorAll('.home-testimonial-card').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [testimonials]);

  // Fade-up — seção CTA
  useEffect(() => {
    if (!ctaRef.current) return;
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          obs.unobserve(e.target);
        }
      }),
      { threshold: 0.2 }
    );
    obs.observe(ctaRef.current.querySelector('.home-cta-inner'));
    return () => obs.disconnect();
  }, []);

  return (
    <div>

      {/* ── HERO — loop fade in/out (7s: 1s in → 5s stay → 1s out) ── */}
      <section className="home-hero">
        <div className="home-hero-anim">
          <img src="/assets/cel.jpg"   alt="Inglês do Dionísio" className="home-hero-img home-hero-mobile"  />
          <img src="/assets/tabpc.jpg" alt="Inglês do Dionísio" className="home-hero-img home-hero-desktop" />
          <div className="home-hero-overlay">
            <div className="home-hero-text">
              <h1>SEJA BEM-VINDO AO<br />INGLÊS DO DIONÍSIO!</h1>
              <p>Sua jornada no inglês começa aqui. Let's go! 🚀</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRESENTATION ── */}
      <section className="home-presentation" ref={presentRef}>
        <div className="home-present-row">
          <div className="home-present-img">
            <img src="/assets/maofrente.png" alt="Inglês nas suas mãos" />
          </div>
          <div className="home-present-text">
            <h2>Hi guys!</h2>
            <p>
              Sejam todos bem-vindos e bem-vindas ao <strong>Inglês do Dionísio!</strong> Fico muito
              feliz em poder ajudar as pessoas a aprender o inglês com o suficiente do meu conhecimento
              e habilidades com o idioma. É o <strong>inglês</strong> nas suas mãos e na ponta da sua língua!
            </p>
          </div>
        </div>

        <div className="home-present-row home-present-row--reverse">
          <div className="home-present-img">
            <img src="/assets/maoesquerda.png" alt="Aprenda inglês" />
          </div>
          <div className="home-present-text">
            <h2>So...</h2>
            <p>
              Eu desenvolvi essa plataforma pra <em>help you guys</em> a aprender e exercitar seu inglês
              com diversas ferramentas — <strong>listening, speaking, writing e reading</strong>. Explore
              a plataforma e voe bem alto na sua jornada com o inglês! <strong>Let's gooooo...</strong>
            </p>
          </div>
        </div>
      </section>

      {/* ── WHY ENGLISH ── */}
      <section className="home-cards-section" ref={cardsRef}>
        <div className="home-cards-bg" style={{ backgroundImage: "url('/assets/bandeirausa.jpeg')" }} />
        <div className="home-cards-content">
          <h2>Por que você <span>precisa</span> aprender inglês?</h2>
          <p className="home-cards-subtitle">6 razões que vão mudar a sua perspectiva</p>
          <div className="home-cards-grid">
            {REASONS.map((r, i) => (
              <div key={i} className="home-card-en">
                <img src={r.img} alt={r.title} />
                <div className="home-card-body">
                  <h3>{r.title}</h3>
                  <p>{r.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRODUCTS ── */}
      {produtos.length > 0 && (
        <section className="home-products">
          <h2 className="home-section-title">Nossos <span>Cursos e Produtos</span></h2>
          <p className="home-section-sub">Tudo o que você precisa para dominar o inglês</p>
          <div className="home-products-grid">
            {produtos.map(p => (
              <div key={p.id} className={`home-produto-card${p.brevemente ? ' home-produto-brevemente' : ''}`}>
                <div className="home-produto-img-wrap">
                  <img
                    src={`/uploads/produtos/${p.imagem}`}
                    alt={p.nome}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                  {p.brevemente ? (
                    <span className="home-badge-brevemente">EM BREVE</span>
                  ) : p.desconto_percentual ? (
                    <span className="home-badge-desconto">-{Math.round(p.desconto_percentual)}%</span>
                  ) : null}
                </div>
                <div className="home-produto-body">
                  <h3>{p.nome}</h3>
                  {p.descricao && <p>{p.descricao.substring(0, 90)}...</p>}
                  <div className="home-produto-price-wrap">
                    {p.preco_original && !p.brevemente && (
                      <span className="home-produto-price-original">
                        {`R$ ${parseFloat(p.preco_original).toFixed(2).replace('.', ',')}`}
                      </span>
                    )}
                    <span className="home-produto-price">
                      {p.precoFormatado || `R$ ${parseFloat(p.preco).toFixed(2)}`}
                    </span>
                  </div>
                  <div className="home-produto-actions">
                    <Link to={`/produto/${p.id}`} className="home-btn-outline">Saber mais</Link>
                    {p.brevemente ? (
                      <span className="home-btn-disabled">Em Breve</span>
                    ) : (
                      <Link to="/carrinho" className="home-btn-primary">Comprar</Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── PALAVRA DO DIA ── */}
      {palavraDoDia && (
        <section style={{ padding: '60px 24px', background: 'linear-gradient(160deg,#0a1628 0%,#1a2a4a 100%)' }}>
          <h2 className="home-section-title" style={{ color: '#fff' }}>
            Palavra do <span>Dia</span>
          </h2>
          <p className="home-section-sub" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Uma nova palavra todos os dias para enriquecer seu vocabulário
          </p>
          <div style={{
            maxWidth: 520, margin: '0 auto',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 20, padding: '32px 28px',
            textAlign: 'center', fontFamily: 'Poppins,sans-serif',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginBottom: 10 }}>
              <h3 style={{ color: '#fff', fontSize: '2.4rem', fontWeight: 800, margin: 0 }}>{palavraDoDia.palavra}</h3>
              <button
                onClick={() => playPalavra(palavraDoDia.palavra)}
                disabled={palavraAudio}
                style={{ background: palavraAudio ? 'rgba(30,144,255,0.5)' : 'rgba(30,144,255,0.25)', border: 'none', borderRadius: 10, padding: '8px 14px', cursor: 'pointer', color: '#7ecfff', fontSize: '1.1rem', transition: 'all 0.2s' }}
              >
                🔊
              </button>
            </div>
            {palavraDoDia.fonetica && (
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem', margin: '0 0 6px', fontStyle: 'italic' }}>{palavraDoDia.fonetica}</p>
            )}
            {palavraDoDia.classe && (
              <span style={{ background: 'rgba(30,144,255,0.2)', color: '#7ecfff', borderRadius: 10, padding: '3px 12px', fontSize: '0.75rem', fontWeight: 700 }}>{palavraDoDia.classe}</span>
            )}
            <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: '14px 18px', margin: '18px 0 0' }}>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Tradução</p>
              <p style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', margin: 0 }}>{palavraDoDia.traducao}</p>
            </div>
            {palavraDoDia.exemplo_en && (
              <div style={{ marginTop: 16, textAlign: 'left' }}>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Exemplo</p>
                <p style={{ color: 'rgba(255,255,255,0.75)', fontStyle: 'italic', fontSize: '0.92rem', margin: 0 }}>"{palavraDoDia.exemplo_en}"</p>
                {palavraDoDia.exemplo_pt && (
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', margin: '6px 0 0' }}>"{palavraDoDia.exemplo_pt}"</p>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── TESTIMONIALS ── */}
      {testimonials.length > 0 && (
        <section className="home-testimonials" ref={testimonialsRef}>
          <h2 className="home-section-title">O que nossos <span>alunos dizem</span></h2>
          <p className="home-section-sub">Histórias reais de quem já transformou o seu inglês</p>
          <div className="home-testimonials-grid">
            {testimonials.map(t => (
              <div key={t.id} className="home-testimonial-card">
                <div className="home-testimonial-top">
                  {t.photo_url ? (
                    <img
                      src={t.photo_url}
                      alt={t.student_name}
                      className="home-testimonial-photo"
                    />
                  ) : (
                    <div className="home-testimonial-avatar">
                      <i className="fas fa-user" />
                    </div>
                  )}
                  <i className="fas fa-quote-left home-testimonial-quote" />
                </div>
                <div className="home-testimonial-body">
                  <h3>{t.student_name}</h3>
                  <p>{t.testimonial_text}</p>
                  <div className="home-testimonial-country">
                    <img
                      src={`https://flagcdn.com/w80/${t.country_code}.png`}
                      alt={t.country_name}
                      width="36"
                      height="25"
                      loading="lazy"
                    />
                    <span>{t.country_name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── CTA ASSINATURA ── */}
      <section className="home-cta" ref={ctaRef}>
        <div className="home-cta-inner">
        <h2>Acesse todo o conteúdo da plataforma</h2>
        <p>Assine o plano Premium e tenha acesso ilimitado a atividades, cursos e muito mais.</p>
        <div className="home-cta-price">
          R$ 29,90 <small>/mês</small>
        </div>
        <ul className="home-cta-benefits">
          <li>Acesso completo a todas as atividades</li>
          <li>Atualizações semanais de conteúdo</li>
          <li>Suporte prioritário</li>
        </ul>
        <Link
          to={user ? '/usuarios' : '/login'}
          className="home-cta-btn"
        >
          {user ? 'Acessar minha conta' : 'Assinar Agora'}
        </Link>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="home-footer">
        <div className="home-footer-grid">
          <div className="home-footer-col">
            <img
              src="/assets/logo_light.png"
              alt="Inglês do Dionísio"
              width="58"
              style={{ borderRadius: '50%', marginBottom: '14px', display: 'block' }}
            />
            <p>A sua plataforma de inglês online com coração brasileiro. 🇧🇷</p>
          </div>

          <div className="home-footer-col">
            <h4>Contato</h4>
            <a href="tel:+5511913255371">📞 +55 (11) 91325-5371</a>
            <a href="mailto:dionisiodacostaelias@gmail.com">✉️ dionisiodacostaelias@gmail.com</a>
          </div>

          <div className="home-footer-col">
            <h4>Navegação</h4>
            <Link to="/">Início</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/produtos">Cursos</Link>
            <Link to={user ? '/usuarios' : '/login'}>{user ? 'Meu Perfil' : 'Login'}</Link>
          </div>

          <div className="home-footer-col">
            <h4>Redes Sociais</h4>
            <div className="home-footer-social">
              <a href="https://www.facebook.com/Inglesdodionosio" target="_blank" rel="noreferrer" aria-label="Facebook">
                <i className="fab fa-facebook-f" />
              </a>
              <a href="https://www.instagram.com/inglesdodionisio/" target="_blank" rel="noreferrer" aria-label="Instagram">
                <i className="fab fa-instagram" />
              </a>
              <a href="https://www.youtube.com/@inglesdodionisio" target="_blank" rel="noreferrer" aria-label="YouTube">
                <i className="fab fa-youtube" />
              </a>
              <a href="https://www.tiktok.com/@inglesdodionosio17" target="_blank" rel="noreferrer" aria-label="TikTok">
                <i className="fab fa-tiktok" />
              </a>
              <a href="https://wa.me/5511913255371" target="_blank" rel="noreferrer" aria-label="WhatsApp">
                <i className="fab fa-whatsapp" />
              </a>
            </div>
          </div>
        </div>

        <div className="home-footer-copy">
          <p>© {new Date().getFullYear()} Inglês do Dionísio. Todos os direitos reservados.</p>
        </div>
      </footer>

    </div>
  );
}
